import {
  getDocs,
  getDoc,
  doc,
  setDoc,
  writeBatch,
  query,
  where,
  collection,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebase/config/firebase';
import { tripsCol, bookingsCol } from '../firebase/firestore/collections';
import { Trip, TripSeat, TripSearchFilter, TripDirection, TripStatus } from '../types';
import { SEED_TRIPS } from '../constants/mockSeed';
import { generateSeatsForCapacity } from '../constants/seatLayout';

export interface DateAvailabilityInfo {
  date: string;
  tripCount: number;
  availableSeatsCount: number;
  hasAvailableSeats: boolean;
  minPrice: number;
  status: 'AVAILABLE' | 'FULL' | 'NO_TRIP';
}

export function isReturnTrip(direction?: string | null): boolean {
  if (!direction) return false;
  const d = String(direction).trim().toUpperCase();
  return d === 'RETURN' || d === 'RET' || direction.includes('عودة');
}

export function isOutboundTrip(direction?: string | null): boolean {
  if (!direction) return true;
  return !isReturnTrip(direction);
}

// Fast in-memory cache for occupied seats: tripId -> { seats: Set<number>, timestamp: number }
const occupiedSeatsCache = new Map<string, { seats: Set<number>; timestamp: number }>();

export const invalidateOccupiedSeatsCache = (tripId?: string) => {
  if (tripId) {
    occupiedSeatsCache.delete(tripId);
  } else {
    occupiedSeatsCache.clear();
  }
};

/**
 * Service managing Trips and Seat Map synchronization
 */
export const tripService = {
  isReturnTrip,
  isOutboundTrip,
  invalidateOccupiedSeatsCache,

  /**
   * Fetch open trips for a specific university with optional date & direction filter
   */
  async getTripsForUniversity(
    universityId: string,
    filters?: TripSearchFilter & { direction?: TripDirection }
  ): Promise<Trip[]> {
    try {
      let q = query(
        tripsCol,
        where('universityId', '==', universityId),
        where('status', 'in', ['OPEN', 'FULL'])
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        let trips = snapshot.docs.map((docSnap) => ({
          ...docSnap.data(),
          id: docSnap.id,
        })) as Trip[];

        if (filters?.date) {
          trips = trips.filter((t) => t.date === filters.date);
        }
        if (filters?.direction) {
          if (filters.direction === 'RETURN') {
            trips = trips.filter((t) => isReturnTrip(t.direction));
          } else {
            trips = trips.filter((t) => isOutboundTrip(t.direction));
          }
        }

        return trips;
      }

      // If empty in Firestore, return matching seed trips immediately
      const matchingSeed = SEED_TRIPS.filter((t) => t.universityId === universityId);
      let res = matchingSeed;
      if (filters?.date) {
        res = res.filter((t) => t.date === filters.date);
      }
      if (filters?.direction) {
        if (filters.direction === 'RETURN') {
          res = res.filter((t) => isReturnTrip(t.direction));
        } else {
          res = res.filter((t) => isOutboundTrip(t.direction));
        }
      }
      return res;
    } catch (err) {
      console.warn('Falling back to local trips seed:', err);
      let seed = SEED_TRIPS.filter((t) => t.universityId === universityId);
      if (filters?.date) {
        seed = seed.filter((t) => t.date === filters.date);
      }
      if (filters?.direction) {
        if (filters.direction === 'RETURN') {
          seed = seed.filter((t) => isReturnTrip(t.direction));
        } else {
          seed = seed.filter((t) => isOutboundTrip(t.direction));
        }
      }
      return seed;
    }
  },

  /**
   * Fast synchronous availability computation from loaded trips
   */
  computeAvailabilityFromTrips(
    trips: Trip[],
    direction?: TripDirection
  ): Record<string, DateAvailabilityInfo> {
    const map: Record<string, DateAvailabilityInfo> = {};
    const filtered = direction
      ? trips.filter((t) => (direction === 'RETURN' ? isReturnTrip(t.direction) : isOutboundTrip(t.direction)))
      : trips;

    for (const t of filtered) {
      if (!t.date) continue;
      const current = map[t.date] || {
        date: t.date,
        tripCount: 0,
        availableSeatsCount: 0,
        hasAvailableSeats: false,
        minPrice: t.price || 0,
        status: 'NO_TRIP' as const,
      };

      current.tripCount += 1;
      const avail = typeof t.availableSeats === 'number' ? t.availableSeats : (t.availableSeatsCount || 0);
      current.availableSeatsCount += avail;
      if (avail > 0 && t.status === 'OPEN') {
        current.hasAvailableSeats = true;
      }
      if (current.minPrice === 0 || (t.price && t.price < current.minPrice)) {
        current.minPrice = t.price;
      }
      map[t.date] = current;
    }

    for (const date in map) {
      if (map[date].hasAvailableSeats) {
        map[date].status = 'AVAILABLE';
      } else if (map[date].tripCount > 0) {
        map[date].status = 'FULL';
      } else {
        map[date].status = 'NO_TRIP';
      }
    }

    return map;
  },

  /**
   * Get availability mapping by date for the calendar
   */
  async getAvailableDatesForUniversity(
    universityId: string,
    direction?: TripDirection
  ): Promise<Record<string, DateAvailabilityInfo>> {
    const allTrips = await this.getTripsForUniversity(universityId, { direction });
    return this.computeAvailabilityFromTrips(allTrips, direction);
  },


  /**
   * Fetch single trip by ID
   */
  async getTripById(tripId: string): Promise<Trip | null> {
    try {
      const docRef = doc(db, 'trips', tripId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        return { ...snap.data(), id: snap.id } as Trip;
      }
    } catch (err) {
      console.warn('Error fetching trip from Firestore:', err);
    }

    const fallback = SEED_TRIPS.find((t) => t.id === tripId) || null;
    return fallback;
  },

  /**
   * Determine all currently occupied seat numbers on a specific trip
   * High performance: parallelized queries + 15s in-memory caching to eliminate lag
   */
  async getOccupiedSeatsForTrip(tripId: string, forceRefresh = false): Promise<Set<number>> {
    const occupied = new Set<number>();
    if (!tripId) return occupied;

    // Check fast cache first
    const cached = occupiedSeatsCache.get(tripId);
    if (!forceRefresh && cached && Date.now() - cached.timestamp < 15000) {
      return new Set(cached.seats);
    }

    const serverNow = Date.now();

    try {
      // Parallel fetch for speed: bookings + seats + locks in 1 roundtrip
      const bQuery = query(bookingsCol, where('tripId', '==', tripId));
      const seatsColRef = collection(db, 'trips', tripId, 'seats');
      const locksColRef = collection(db, 'trips', tripId, 'seatLocks');

      const [bSnapResult, seatsSnapResult, locksSnapResult] = await Promise.allSettled([
        getDocs(bQuery),
        getDocs(seatsColRef),
        getDocs(locksColRef),
      ]);

      // 1. Process bookings
      if (bSnapResult.status === 'fulfilled') {
        bSnapResult.value.forEach((bDoc) => {
          const b = bDoc.data() as any;
          const status = b.status || b.bookingStatus;
          const pStatus = b.paymentStatus;
          const isCancelled =
            status === 'CANCELLED' ||
            status === 'REJECTED' ||
            status === 'DELETED' ||
            pStatus === 'CANCELLED' ||
            pStatus === 'REJECTED' ||
            pStatus === 'DELETED';

          if (!isCancelled && b.seatNumber) {
            const sNum = Number(b.seatNumber);
            if (sNum > 0) occupied.add(sNum);
          }
        });
      }

      // 2. Process subcollection seats
      if (seatsSnapResult.status === 'fulfilled') {
        seatsSnapResult.value.forEach((docSnap) => {
          const s = docSnap.data() as TripSeat;
          const seatNum = Number(s.seatNumber || docSnap.id);
          if (seatNum > 0) {
            if (s.status === 'BOOKED') {
              occupied.add(seatNum);
            } else if (s.status === 'HELD' && s.holdExpiresAt) {
              const exp = new Date(s.holdExpiresAt).getTime();
              if (exp > serverNow) {
                occupied.add(seatNum);
              }
            }
          }
        });
      }

      // 3. Process subcollection seatLocks
      if (locksSnapResult.status === 'fulfilled') {
        locksSnapResult.value.forEach((lDoc) => {
          const l = lDoc.data() as any;
          const exp = l.holdExpiresAt ? new Date(l.holdExpiresAt).getTime() : 0;
          if (exp > serverNow) {
            const sNum = Number(l.seatNumber || lDoc.id);
            if (sNum > 0) occupied.add(sNum);
          }
        });
      }
    } catch (e) {
      console.warn(`Error fetching occupied seats for trip ${tripId}:`, e);
    }

    // Save to cache
    occupiedSeatsCache.set(tripId, { seats: new Set(occupied), timestamp: Date.now() });

    return occupied;
  },

  /**
   * Batch fetch occupied seats for multiple trips in parallel (Lightning-fast for Multi-Day Wizard)
   */
  async getOccupiedSeatsForMultipleTrips(tripIds: string[]): Promise<Record<string, Set<number>>> {
    const uniqueIds = Array.from(new Set(tripIds.filter(Boolean)));
    const results: Record<string, Set<number>> = {};

    await Promise.all(
      uniqueIds.map(async (tId) => {
        results[tId] = await this.getOccupiedSeatsForTrip(tId);
      })
    );

    return results;
  },

  /**
   * Find the lowest available sequential seat (1..capacity) strictly for this individual trip.
   * If a student booked Sunday only and gets Seat 1, another student booking Sunday & Monday
   * will get Seat 2 on Sunday (since 1 is taken) and Seat 1 on Monday (since 1 is free).
   */
  async getNextAvailableSeatForTrip(
    tripId: string,
    capacity: number = 50,
    claimedSeats: number[] = []
  ): Promise<number> {
    if (!tripId) return 1;
    const occupied = await this.getOccupiedSeatsForTrip(tripId);
    claimedSeats.forEach((s) => occupied.add(s));

    const maxCap = Math.max(capacity, 1);
    for (let seatNum = 1; seatNum <= maxCap; seatNum++) {
      if (!occupied.has(seatNum)) {
        return seatNum;
      }
    }

    // Bus is full, return capacity
    return maxCap;
  },

  /**
   * Fetch real-time seat configuration and status for a trip
   */
  async getTripSeats(tripId: string, customCapacity?: number): Promise<TripSeat[]> {
    try {
      const seatsColRef = collection(db, 'trips', tripId, 'seats');
      const snap = await getDocs(seatsColRef);

      let seats: TripSeat[] = [];
      if (!snap.empty) {
        seats = snap.docs.map((docSnap) => {
          const seatData = docSnap.data() as TripSeat;
          return {
            id: docSnap.id,
            seatNumber: seatData.seatNumber,
            tripId: seatData.tripId || tripId,
            status: seatData.status || 'AVAILABLE',
            updatedAt: seatData.updatedAt || new Date().toISOString(),
          } as TripSeat;
        });
      } else {
        seats = await this.initializeTripSeatsIfMissing(tripId, customCapacity);
      }

      // Reconcile with occupied seats from both bookings & seats
      const occupied = await this.getOccupiedSeatsForTrip(tripId);
      return seats.map((s) => {
        if (occupied.has(s.seatNumber)) {
          return { ...s, status: 'BOOKED' as const };
        }
        return s;
      });
    } catch (err) {
      console.warn('Error fetching seats from Firestore, generating default layout:', err);
      return generateSeatsForCapacity(customCapacity || 50).map((seatDef) => ({
        id: String(seatDef.seatNumber),
        seatNumber: seatDef.seatNumber,
        tripId,
        status: 'AVAILABLE',
        updatedAt: new Date().toISOString(),
      }));
    }
  },

  /**
   * Real-time listener for seat map updates
   */
  subscribeTripSeats(
    tripId: string,
    onUpdate: (seats: TripSeat[]) => void
  ): Unsubscribe {
    const seatsColRef = collection(db, 'trips', tripId, 'seats');

    return onSnapshot(
      seatsColRef,
      (snapshot) => {
        const seats = snapshot.docs.map((docSnap) => {
          const seatData = docSnap.data() as TripSeat;
          return {
            id: docSnap.id,
            seatNumber: seatData.seatNumber,
            tripId: seatData.tripId || tripId,
            status: seatData.status || 'AVAILABLE',
            updatedAt: seatData.updatedAt || new Date().toISOString(),
          } as TripSeat;
        });

        if (seats.length > 0) {
          onUpdate(seats);
        }
      },
      (error) => {
        console.warn('Error in seat real-time subscription:', error);
      }
    );
  },

  /**
   * Helper initializing passenger seats matching the trip's exact capacity
   */
  async initializeTripSeatsIfMissing(tripId: string, customCapacity?: number): Promise<TripSeat[]> {
    let capacity = customCapacity;
    if (!capacity) {
      try {
        const tripSnap = await getDoc(doc(db, 'trips', tripId));
        if (tripSnap.exists()) {
          const tData = tripSnap.data();
          capacity = tData.totalSeats || tData.capacity || 50;
        }
      } catch {}
    }
    const finalCapacity = capacity || 50;
    const seatDefs = generateSeatsForCapacity(finalCapacity);
    const initialSeats: TripSeat[] = [];
    const nowIso = new Date().toISOString();

    for (const seatDef of seatDefs) {
      const seat: TripSeat = {
        id: String(seatDef.seatNumber),
        seatNumber: seatDef.seatNumber,
        tripId,
        status: 'AVAILABLE',
        updatedAt: nowIso,
      };
      initialSeats.push(seat);

      try {
        const seatRef = doc(db, 'trips', tripId, 'seats', String(seatDef.seatNumber));
        await setDoc(seatRef, seat, { merge: true });
      } catch (err) {
        // Ignored in read-only sandbox mode
      }
    }

    return initialSeats;
  },

  /**
   * Bulk update trips in Firestore batches of up to 400 docs per commit
   */
  async bulkUpdateTrips(
    tripsToUpdate: Trip[],
    updates: BulkTripUpdateFields
  ): Promise<BulkTripsResult> {
    if (!tripsToUpdate || tripsToUpdate.length === 0) {
      return { success: true, totalMatched: 0, updatedCount: 0, skippedCount: 0, errors: [] };
    }

    const errors: string[] = [];
    let updatedCount = 0;
    const nowIso = new Date().toISOString();

    const chunkSize = 400;
    for (let i = 0; i < tripsToUpdate.length; i += chunkSize) {
      const chunk = tripsToUpdate.slice(i, i + chunkSize);
      const batch = writeBatch(db);

      for (const trip of chunk) {
        try {
          const tripRef = doc(db, 'trips', trip.id);
          const payload: Record<string, any> = {
            updatedAt: nowIso,
          };

          // 1. Price
          if (updates.priceMode === 'SET' && updates.price !== undefined && updates.price > 0) {
            payload.price = updates.price;
          } else if (updates.priceMode === 'SUBTRACT' && updates.priceDelta !== undefined) {
            const currentPrice = trip.price || 150;
            payload.price = Math.max(1, currentPrice - updates.priceDelta);
          } else if (updates.priceMode === 'ADD' && updates.priceDelta !== undefined) {
            const currentPrice = trip.price || 150;
            payload.price = currentPrice + updates.priceDelta;
          } else if (updates.price !== undefined && updates.price > 0) {
            payload.price = updates.price;
          }

          // 2. Times
          if (updates.departureTime) {
            payload.departureTime = updates.departureTime.trim();
          }
          if (updates.expectedArrivalTime) {
            payload.expectedArrivalTime = updates.expectedArrivalTime.trim();
            payload.arrivalTime = updates.expectedArrivalTime.trim();
          }

          // 3. Stations & Route
          if (updates.origin) {
            payload.origin = updates.origin.trim();
            payload.pickupLocation = updates.origin.trim();
          }
          if (updates.destination) {
            payload.destination = updates.destination.trim();
            payload.destinationDetails = updates.destination.trim();
          }
          if (updates.launchStation) {
            payload.launchStation = updates.launchStation.trim();
          }

          // 4. Vehicle & Capacity
          if (updates.vehicleId) {
            payload.vehicleId = updates.vehicleId.trim();
          }
          if (updates.vehiclePlate) {
            payload.vehiclePlate = updates.vehiclePlate.trim();
            payload.vehicleNumber = updates.vehiclePlate.trim();
          }
          if (updates.driverName !== undefined) {
            payload.driverName = updates.driverName.trim();
          }
          if (updates.driverPhone !== undefined) {
            payload.driverPhone = updates.driverPhone.trim();
          }

          if (updates.totalSeats !== undefined && updates.totalSeats > 0) {
            const newTotalSeats = updates.totalSeats;
            const currentTotal = trip.totalSeats || 50;
            const currentAvail = trip.availableSeats !== undefined ? trip.availableSeats : currentTotal;
            const bookedCount = Math.max(0, currentTotal - currentAvail);

            payload.totalSeats = newTotalSeats;
            payload.capacity = newTotalSeats;
            payload.availableSeats = Math.max(0, newTotalSeats - bookedCount);
            payload.availableSeatsCount = payload.availableSeats;
          }

          // 5. Status
          if (updates.status) {
            payload.status = updates.status;
          }

          batch.update(tripRef, payload);
          invalidateOccupiedSeatsCache(trip.id);
          updatedCount++;
        } catch (err: any) {
          errors.push(`فشل تحديث الرحلة ${trip.tripCode || trip.id}: ${err?.message || err}`);
        }
      }

      try {
        await batch.commit();
      } catch (commitErr: any) {
        errors.push(`فشل حفظ دفعة التحديثات: ${commitErr?.message || commitErr}`);
      }
    }

    return {
      success: errors.length === 0,
      totalMatched: tripsToUpdate.length,
      updatedCount,
      skippedCount: tripsToUpdate.length - updatedCount,
      errors,
    };
  },

  /**
   * Bulk delete trips in Firestore batches
   */
  async bulkDeleteTrips(
    tripsToDelete: Trip[],
    options?: { skipTripsWithBookings?: boolean }
  ): Promise<BulkTripsResult> {
    if (!tripsToDelete || tripsToDelete.length === 0) {
      return { success: true, totalMatched: 0, updatedCount: 0, skippedCount: 0, errors: [] };
    }

    const errors: string[] = [];
    let deletedCount = 0;
    let skippedCount = 0;

    const chunkSize = 400;
    for (let i = 0; i < tripsToDelete.length; i += chunkSize) {
      const chunk = tripsToDelete.slice(i, i + chunkSize);
      const batch = writeBatch(db);

      for (const trip of chunk) {
        const bookedCount = Math.max(0, (trip.totalSeats || 50) - (trip.availableSeats || 0));
        if (options?.skipTripsWithBookings && bookedCount > 0) {
          skippedCount++;
          continue;
        }

        try {
          const tripRef = doc(db, 'trips', trip.id);
          batch.delete(tripRef);
          invalidateOccupiedSeatsCache(trip.id);
          deletedCount++;
        } catch (err: any) {
          errors.push(`فشل حذف الرحلة ${trip.tripCode || trip.id}: ${err?.message || err}`);
        }
      }

      try {
        await batch.commit();
      } catch (commitErr: any) {
        errors.push(`فشل تنفيذ دفعة الحذف: ${commitErr?.message || commitErr}`);
      }
    }

    return {
      success: errors.length === 0,
      totalMatched: tripsToDelete.length,
      updatedCount: 0,
      deletedCount,
      skippedCount,
      errors,
    };
  },
};

export interface BulkTripUpdateFields {
  price?: number;
  priceDelta?: number;
  priceMode?: 'SET' | 'ADD' | 'SUBTRACT';
  departureTime?: string;
  expectedArrivalTime?: string;
  origin?: string;
  destination?: string;
  launchStation?: string;
  vehicleId?: string;
  vehiclePlate?: string;
  vehicleNumber?: string;
  driverName?: string;
  driverPhone?: string;
  totalSeats?: number;
  status?: TripStatus;
}

export interface BulkTripsResult {
  success: boolean;
  totalMatched: number;
  updatedCount: number;
  skippedCount: number;
  deletedCount?: number;
  errors: string[];
}

